import { useEffect } from 'react';
import dayjs from 'dayjs';
import { apiGet } from '../api';
import { useAuth } from '../context/useAuth';
import { useNotification } from '../context/NotificationContext';

/**
 * useSessionWatch Hook
 * 
 * Periodically pings the backend to verify session validity.
 * If the session is revoked (e.g., via Single Session Active kick),
 * the apiFetch wrapper in api/index.js will intercept the 401 
 * and dispatch the 'session-revoked' event, triggering the modal.
 */
export function useSessionWatch() {
    const { token, user, lastActivity, logout } = useAuth();
    const { addNotification } = useNotification();

    useEffect(() => {
        if (!token || !user) return;

        const interval = setInterval(async () => {
            // Mandatory polling to detect server-side changes even when idle
            try {
                await apiGet('/users/me', token);
            } catch (err) {
                console.debug('[SessionWatch] Heartbeat check failed:', err);
            }
        }, 60000); // Ping every 60 seconds

        return () => clearInterval(interval);
    }, [token, user]); // Removed lastActivity to poll always

    // Handle forceReauth / grace period globally via event listener
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;

        const handleGracePeriod = (expiresTs: number) => {
            const nowTs = dayjs().unix();
            const remainingSeconds = expiresTs - nowTs;

            if (remainingSeconds <= 0) {
                logout();
                return;
            }

            // Deduplicate across tabs using localStorage
            const storageKey = `grace_notified_${expiresTs}`;
            if (!localStorage.getItem(storageKey)) {
                localStorage.setItem(storageKey, 'true');
                addNotification("Your password has been changed. Please re-login within 5 minutes.", "warning");
            }

            // Set timeout to auto logout
            if (timer) clearTimeout(timer);
            const timeoutMs = remainingSeconds * 1000;
            timer = setTimeout(() => {
                const hasUnsaved = window.confirm("Your session has expired due to a password change. Any unsaved changes may be lost. Click OK to log out.");
                logout();
            }, timeoutMs);
        };

        const onGracePeriodEvent = (e: any) => {
            handleGracePeriod(e.detail.expiresAt);
        };

        window.addEventListener('grace-period-warning', onGracePeriodEvent);

        // Also check if the user object itself has the data (from initial load or polling)
        if (user?.forceReauth && user?.gracePeriodExpiresAt) {
            handleGracePeriod(user.gracePeriodExpiresAt);
        }

        // Listen for localStorage changes from other tabs to sync logout immediately
        const onStorageChange = (e: StorageEvent) => {
            // If another tab triggers the same storage key, we don't necessarily need to do anything extra
            // since the token sync event in api/index.js handles actual logouts already.
        };
        window.addEventListener('storage', onStorageChange);

        return () => {
            window.removeEventListener('grace-period-warning', onGracePeriodEvent);
            window.removeEventListener('storage', onStorageChange);
            if (timer) clearTimeout(timer);
        };
    }, [user?.forceReauth, user?.gracePeriodExpiresAt, logout, addNotification]);
}
